import React from 'react';
import { usePricing } from '@/contexts/PricingContext';

interface WelcomeKitItem {
  name: string;
  description: string;
  image: string;
}

export function BundleShowcase() {
  const pricing = usePricing();
  
  const welcomeKitItems: WelcomeKitItem[] = [
    {
      name: 'Recovery Tag',
      description: 'Battery-free, ultra-resistant backup solution',
      image: 'https://ryder.id/cdn/shop/files/recovery-tag.png?v=1770649962',
    },
    {
      name: 'Wireless Charger',
      description: 'Universal charger for Ryder One and smartphones',
      image: 'https://ryder.id/cdn/shop/files/wireless_charging_1.png?v=1769270696',
    },
    {
      name: 'Travel Pouch',
      description: 'Premium protection for your wallet on the go',
      image: 'https://private-us-east-1.manuscdn.com/sessionFile/zt0R2JLN6ZCkpO8tpr5PKy/sandbox/0k5yXezDwWrqUz1YRi290F-img-1_1771378546000_na1fn_dHJhdmVsLXBvdWNo.png?x-oss-process=image/resize,w_1920,h_1920/format,webp/quality,q_80&Expires=1798761600&Policy=eyJTdGF0ZW1lbnQiOlt7IlJlc291cmNlIjoiaHR0cHM6Ly9wcml2YXRlLXVzLWVhc3QtMS5tYW51c2Nkbi5jb20vc2Vzc2lvbkZpbGUvenQwUjJKTE42WkNrcE84dHByNVBLeS9zYW5kYm94LzBrNXlYZXpEd1dycVV6MVlSaTI5MEYtaW1nLTFfMTc3MTM3ODU0NjAwMF9uYTFmbl9kSEpoZG1Wc0xYQnZkV05vLnBuZz94LW9zcy1wcm9jZXNzPWltYWdlL3Jlc2l6ZSx3XzE5MjAsaF8xOTIwL2Zvcm1hdCx3ZWJwL3F1YWxpdHkscV84MCIsIkNvbmRpdGlvbiI6eyJEYXRlTGVzc1RoYW4iOnsiQVdTOkVwb2NoVGltZSI6MTc5ODc2MTYwMH19fV19&Key-Pair-Id=K2HSFNDJXOU9YS&Signature=Jij~inRxwiP1DodsfzQgXXTg8aveICIdKnYB0qZgnz4RLHazbHaUshDIqgWeON-hxMN-K4vfYDJBwPJylLurGBu96EOQyAItO6VYOzcJ3MBmQCakdFsgzMtOtvlXs4FQfm7FVHIMIkuZnk9BjU8HXPnioRG20npX1toICRQZ0iAzLdAcbU84T-Eu1B9keKlMdFoqNgqlFQ7BuTf2B8z-xnD36fH11x5C3rzJ0UcsiVoL3q~EMnh7LMcliwm0nl9CrsmTrYVWrBnQDbC-sUNtFv7njXh11JwrG9hQUI2-hrXKHnoqAXlwfW3J15NyLKs6mrGO398~yJXrDzhV7PH-jw__',
    }
  ];

  return (
    <div className="py-4">
      <p className="text-sm font-semibold mb-3 text-foreground" style={{ fontFamily: 'Poppins, sans-serif' }}>
        Included in your order
      </p>
      
      {/* Boxed bundle container with premium styling */}
      <div className="border border-border rounded-2xl p-3 bg-gradient-to-br from-white to-gray-50/30 shadow-lg">
        <div className="space-y-3">
          {/* Main Product - Ryder One (prominent) */}
          <div 
            className="flex items-center gap-3 p-3 rounded-xl bg-white border-2 border-[#6a63f5]/20 hover:border-[#6a63f5]/30 transition-colors"
          >
            <img 
              src="https://files.manuscdn.com/user_upload_by_module/session_file/310519663243023818/bfMrjDxrEMyxijvL.jpg"
              alt="Ryder One"
              className="w-[80px] h-[80px] object-cover rounded-lg bg-gradient-to-br from-gray-50 to-white border border-gray-100 flex-shrink-0"
              loading="lazy"
            />
            <div className="flex-1 min-w-0">
              <h5 className="text-sm font-bold text-foreground leading-tight mb-1" style={{ fontFamily: 'Poppins, sans-serif' }}>
                Ryder One
              </h5>
              <p className="text-[11px] text-muted-foreground leading-snug line-clamp-1">
                Hardware wallet with EAL6+ security certification
              </p>
            </div>
            
            <div className="flex flex-col items-end gap-0 shrink-0">
              <span className="text-xs font-bold text-[#171717]">
                ${pricing.salePrice}
              </span>
              <span className="text-[10px] text-muted-foreground line-through">
                ${pricing.originalPrice}.00
              </span>
            </div>
          </div>

          {/* Free Welcome Kit */}
          <div className="bg-gray-50/50 rounded-lg p-2.5 space-y-1.5">
            <div className="flex items-center gap-1.5 mb-1.5">
              <span className="text-[10px] font-bold text-[#22c55e] uppercase tracking-wide">🎁 Free welcome kit</span>
              <span className="text-[9px] text-muted-foreground">— included with every order</span>
            </div>
            {welcomeKitItems.map((item, index) => (
              <div 
                key={index}
                className="flex items-center gap-2 py-1.5 px-2 rounded-lg bg-white/60 border border-gray-100/50"
              >
                <img 
                  src={item.image} 
                  alt={item.name}
                  className="w-[52px] h-[52px] object-cover rounded-md bg-white border border-gray-100 flex-shrink-0"
                  loading="lazy"
                />
                <div className="flex-1 min-w-0">
                  <h5 className="text-[11px] font-bold text-foreground leading-tight" style={{ fontFamily: 'Poppins, sans-serif' }}>
                    {item.name}
                  </h5>
                  <p className="text-[9px] text-muted-foreground leading-tight line-clamp-1">
                    {item.description}
                  </p>
                </div>
                
                <span className="text-[10px] font-bold text-[#22c55e] shrink-0">
                  FREE
                </span>
              </div>
            ))}
          </div>
        </div>
        
        {/* Clear pricing display */}
        <div className="mt-4 pt-4 border-t-2 border-dashed border-gray-200 space-y-2 bg-gradient-to-br from-indigo-50/50 to-purple-50/30 -mx-3 -mb-3 px-3 pb-4 rounded-b-2xl">
          {/* Regular price */}
          <div className="flex items-center justify-between">
            <span className="text-xs text-muted-foreground">Regular price:</span>
            <span className="text-base font-semibold text-muted-foreground line-through">
              ${pricing.originalPrice}
            </span>
          </div>
          
          {/* You Pay - Most prominent */}
          <div className="flex items-center justify-between py-1">
            <span className="text-base font-bold text-foreground">You pay:</span>
            <span className="text-3xl font-black text-[#6a63f5]">
              ${pricing.salePrice}
            </span>
          </div>
          
          {/* Savings */}
          <div className="flex items-center justify-between">
            <span className="text-xs text-muted-foreground">You save:</span>
            <span className="text-lg font-bold text-[#22c55e]">
              ${pricing.savingsAmount} <span className="text-sm">({pricing.discountPercent}% off)</span>
            </span>
          </div>
          
          {/* Welcome kit note */}
          <p className="text-[10px] text-muted-foreground text-center pt-1">
            + free welcome kit (Recovery Tag, Wireless Charger, Travel Pouch)
          </p>
        </div>
      </div>
    </div>
  );
}
